import { useState, useEffect, useCallback } from 'react';
import { auth, db } from '../firebase/firebaseConfig';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import {
  parseSubscriptionPeriodEnd,
  subscriptionDocGrantsPremiumAccess,
  partnerProUntilGrantsPremiumAccess,
} from '../utils/subscriptionEntitlement';

/**
 * Enhanced hook to check premium status using multiple verification methods
 * Based on the comprehensive approach used in SubscriptionPage.js
 *
 * @returns {Object} Premium status information including isPremium flag and plan details
 */
export default function useEnhancedPremiumStatus() {
  const [isPremium, setIsPremium] = useState(false);
  const [hasPaidSubscription, setHasPaidSubscription] = useState(false);
  const [planType, setPlanType] = useState(null);
  const [partnerProUntil, setPartnerProUntil] = useState(null);
  const [partnerCode, setPartnerCode] = useState(null);
  const [partnerName, setPartnerName] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState({
    id: null,
    cancelAtPeriodEnd: false,
    currentPeriodEnd: null,
    status: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const refreshPremiumStatus = useCallback(() => {
    setRefreshNonce((value) => value + 1);
  }, []);

  useEffect(() => {
    const checkPremiumStatus = async () => {
      setIsLoading(true);
      /** Firestore UID the in-flight request targets; avoids applying results after sign-out/account switch */
      let lockedUid = null;

      try {
        const currentUser = auth.currentUser;

        if (!currentUser) {
          setIsPremium(false);
          setHasPaidSubscription(false);
          setPlanType(null);
          setPartnerProUntil(null);
          setPartnerCode(null);
          setPartnerName(null);
          setSubscriptionData({
            id: null,
            cancelAtPeriodEnd: false,
            currentPeriodEnd: null,
            status: null
          });
          setIsLoading(false);
          return;
        }

        lockedUid = currentUser.uid;

        const subscriptionsRef = collection(db, `users/${lockedUid}/subscriptions`);
        const userRef = doc(db, 'users', lockedUid);
        const [subscriptionsSnapshot, userSnap] = await Promise.all([
          getDocs(subscriptionsRef),
          getDoc(userRef),
        ]);

        if (auth.currentUser?.uid !== lockedUid) {
          return;
        }

        const now = new Date();
        let userIsPremium = false;
        let userHasPaid = false;
        let userPlanType = 'monthly';
        let subData = {
          id: null,
          cancelAtPeriodEnd: false,
          currentPeriodEnd: null,
          status: null
        };

        if (!subscriptionsSnapshot.empty) {
          for (const docSnapshot of subscriptionsSnapshot.docs) {
            const subscription = docSnapshot.data();

            if (!subscriptionDocGrantsPremiumAccess(subscription, now)) continue;

            userHasPaid = true;
            userIsPremium = true;

            const firstItem = subscription.items?.[0];
            const interval =
              firstItem?.plan?.interval ||
              firstItem?.price?.recurring?.interval ||
              null;
            const planTypeMeta =
              firstItem?.plan?.metadata?.type ||
              firstItem?.price?.metadata?.type ||
              null;
            if (interval === 'year' || planTypeMeta === 'annual') {
              userPlanType = 'annual';
            }

            const currentPeriodEnd = parseSubscriptionPeriodEnd(subscription.current_period_end);
            subData = {
              id: docSnapshot.id,
              cancelAtPeriodEnd: subscription.cancel_at_period_end || false,
              currentPeriodEnd,
              status: subscription.status
            };

            break;
          }
        }

        const userData = userSnap.exists() ? userSnap.data() : {};
        const partnerUntil = parseSubscriptionPeriodEnd(userData.partnerProUntil);
        const redeemedCode = typeof userData.partnerCode === 'string' ? userData.partnerCode : null;
        const redeemedPartnerName = typeof userData.partnerName === 'string' ? userData.partnerName : null;
        const partnerActive = partnerProUntilGrantsPremiumAccess(userData.partnerProUntil, now);

        if (partnerActive) {
          userIsPremium = true;
        }

        if (auth.currentUser?.uid !== lockedUid) {
          return;
        }

        setIsPremium(userIsPremium);
        setHasPaidSubscription(userHasPaid);
        setPlanType(userHasPaid ? userPlanType : null);
        setPartnerProUntil(partnerActive ? partnerUntil : null);
        setPartnerCode(redeemedCode);
        setPartnerName(partnerActive ? redeemedPartnerName : null);
        setSubscriptionData(subData);

        if (userHasPaid) {
          localStorage.setItem(`${lockedUid}_planType`, userPlanType);
        } else {
          localStorage.removeItem(`${lockedUid}_planType`);
        }
      } catch (error) {
        const staleMidFlight =
          lockedUid != null &&
          auth.currentUser?.uid !== lockedUid &&
          error?.code === 'permission-denied';
        if (staleMidFlight) {
          return;
        }
        console.error('Error checking premium status:', error);
        setIsPremium(false);
        setHasPaidSubscription(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkPremiumStatus();

    const unsubscribe = auth.onAuthStateChanged(() => {
      checkPremiumStatus();
    });

    return () => {
      unsubscribe();
    };
  }, [refreshNonce]);

  return {
    isPremium,
    hasPaidSubscription,
    planType,
    partnerProUntil,
    partnerCode,
    partnerName,
    subscriptionData,
    isLoading,
    refreshPremiumStatus,
  };
}
